import { useState } from "react";
import "./RetirementCalculator.css";

const inr   = (n) => "₹" + Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const crore = (n) => n >= 10000000 ? "₹" + (n / 10000000).toFixed(2) + " Cr" : inr(n);

const FORM_TABS  = ["Timeline", "Finances", "Returns", "Investments"];
const FORM_ICONS = ["👤", "💸", "📈", "🏛"];
const RES_TABS   = ["Overview", "Corpus Build", "Year-wise"];

/* ── small helpers ── */
function Field({ label, children }) {
  return (
    <div className="ret-field">
      <label className="ret-field-label">{label}</label>
      {children}
    </div>
  );
}

function RupeeInput({ value, onChange, placeholder }) {
  return (
    <div className="ret-inp-wrap">
      <span className="ret-inp-sym">₹</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, leftLabel, rightLabel }) {
  return (
    <Field label={<>{label} <span className="ret-val-badge">{value}%</span></>}>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(+e.target.value)} />
      <div className="ret-range-labels"><span>{leftLabel}</span><span>{rightLabel}</span></div>
    </Field>
  );
}

function Toggle({ options, value, onChange }) {
  return (
    <div className="ret-toggle-group">
      {options.map((v) => (
        <button key={v}
          className={`ret-tog${+value === v ? " active" : ""}`}
          onClick={() => onChange(v)}>{v}</button>
      ))}
    </div>
  );
}

function StatCard({ label, value, note, accent, colorClass }) {
  return (
    <div className={`ret-stat-card${accent ? " ret-stat-card--accent" : ""}`}>
      <div className="ret-stat-lbl">{label}</div>
      <div className={`ret-stat-val${colorClass ? " " + colorClass : ""}`}>{value}</div>
      {note && <div className="ret-stat-note">{note}</div>}
    </div>
  );
}

export default function RetirementCalculator() {
  const [formTab, setFormTab] = useState(0);
  const [resTab,  setResTab]  = useState(0);
  const [form, setForm] = useState({
    currentAge: 30, retirementAge: 60, lifeExpectancy: 85,
    monthlyExpenses: 30010, monthlySavings: 20000,
    inflation: 6, preReturn: 10, postReturn: 7,
    currentSavings: 300100, epf: 0, ppf: 0, nps: 0, other: 0,
  });
  const [result, setResult] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k) => parseFloat(form[k]) || 0;

  const workYrs = +form.retirementAge - +form.currentAge;
  const retYrs  = +form.lifeExpectancy - +form.retirementAge;
  const existingTotal = num("currentSavings") + num("epf") + num("ppf") + num("nps") + num("other");

  function calculate() {
    if (workYrs <= 0 || retYrs <= 0) return;
    const inf     = num("inflation") / 100;
    const preRet  = num("preReturn") / 100;
    const postRet = num("postReturn") / 100;
    const moExp   = num("monthlyExpenses");
    const annExp  = moExp * 12;
    const moSav   = num("monthlySavings");

    const futureAnnExp = annExp * Math.pow(1 + inf, workYrs);
    const futureMoExp  = futureAnnExp / 12;
    const realRate     = (1 + postRet) / (1 + inf) - 1;
    const pvFactor     = realRate > 0 ? (1 - Math.pow(1 + realRate, -retYrs)) / realRate : retYrs;
    const corpusNeeded = futureAnnExp * pvFactor;

    const fvExisting = existingTotal * Math.pow(1 + preRet, workYrs);
    const mRate      = preRet / 12;
    const months     = workYrs * 12;
    const fvSavings  = moSav > 0 && months > 0
      ? moSav * ((Math.pow(1 + mRate, months) - 1) / mRate) * (1 + mRate) : 0;
    const totalCorpus = fvExisting + fvSavings;

    const surplus      = totalCorpus - corpusNeeded;
    const isOnTrack    = surplus >= 0;
    const addlMonthly  = !isOnTrack && mRate > 0 && months > 0
      ? (Math.abs(surplus) * mRate) / ((Math.pow(1 + mRate, months) - 1) * (1 + mRate)) : 0;
    const monthlyIncome = (totalCorpus * postRet) / 12;
    const coverageYears = realRate > 0
      ? -Math.log(1 - (totalCorpus * realRate) / futureAnnExp) / Math.log(1 + realRate)
      : totalCorpus / futureAnnExp;

    const yearlyData = [];
    let bal = existingTotal;
    for (let y = 0; y <= workYrs; y++) {
      const needed = annExp * Math.pow(1 + inf, y) * pvFactor;
      yearlyData.push({ y, age: +form.currentAge + y, corpus: Math.round(bal), needed: Math.round(needed) });
      if (y < workYrs) bal = bal * (1 + preRet) + moSav * 12;
    }

    setResult({
      corpusNeeded, totalCorpus, surplus, isOnTrack,
      addlMonthly, fvExisting, fvSavings,
      futureMoExp, monthlyIncome,
      workYrs, retYrs, existingTotal, yearlyData,
      coverageYears: Math.min(coverageYears, 100),
    });
    setResTab(0);
  }

  /* ── tab content ── */
  const tabContent = {
    0: (
      <>
        <div className="ret-grid2">
          <Field label="Current Age">
            <input type="number" className="ret-plain-inp" min="20" max="60"
              value={form.currentAge} onChange={(e) => upd("currentAge", e.target.value)} />
          </Field>
          <Field label="Retirement Age">
            <input type="number" className="ret-plain-inp" min="45" max="75"
              value={form.retirementAge} onChange={(e) => upd("retirementAge", e.target.value)} />
          </Field>
        </div>
        <Field label="Life Expectancy">
          <Toggle options={[70, 75, 80, 85, 90]} value={form.lifeExpectancy}
            onChange={(v) => upd("lifeExpectancy", v)} />
        </Field>
        {workYrs > 0 && retYrs > 0 && (
          <div className="ret-timeline-chips">
            <span>🏃 {workYrs} yrs to retirement</span>
            <span>🏖 {retYrs} yrs of retirement</span>
          </div>
        )}
        <div className="ret-info-box">
          <p>💡 <strong>Rule of 25:</strong> You need 25× your annual expenses as retirement corpus.</p>
          <p>💡 <strong>Rule of 4%:</strong> Withdraw 4% annually for inflation-safe income.</p>
        </div>
      </>
    ),
    1: (
      <>
        <Field label={<>Current Monthly Expenses <span className="ret-val-badge">{inr(+form.monthlyExpenses)}</span></>}>
          <RupeeInput value={form.monthlyExpenses} onChange={(v) => upd("monthlyExpenses", v)} />
          <input type="range" min="10000" max="300000" step="3001" value={form.monthlyExpenses}
            onChange={(e) => upd("monthlyExpenses", +e.target.value)} />
          <div className="ret-range-labels"><span>₹10K</span><span>₹3L</span></div>
        </Field>
        <Field label={<>Monthly Investment / SIP <span className="ret-val-badge">{inr(+form.monthlySavings)}</span></>}>
          <RupeeInput value={form.monthlySavings} onChange={(v) => upd("monthlySavings", v)} />
          <input type="range" min="1000" max="200000" step="1000" value={form.monthlySavings}
            onChange={(e) => upd("monthlySavings", +e.target.value)} />
          <div className="ret-range-labels"><span>₹1K</span><span>₹2L</span></div>
        </Field>
      </>
    ),
    2: (
      <>
        <SliderRow label="Inflation Rate" value={form.inflation} min={4} max={10} step={0.5}
          onChange={(v) => upd("inflation", v)} leftLabel="4% (Low)" rightLabel="10% (High)" />
        <SliderRow label="Pre-Retirement Returns" value={form.preReturn} min={6} max={16} step={0.5}
          onChange={(v) => upd("preReturn", v)} leftLabel="6% (Conservative)" rightLabel="16% (Aggressive)" />
        <SliderRow label="Post-Retirement Returns" value={form.postReturn} min={4} max={10} step={0.5}
          onChange={(v) => upd("postReturn", v)} leftLabel="4% (FD/Debt)" rightLabel="10% (Equity)" />
        <div className="ret-info-box">
          <p>💡 Higher post-retirement returns allow smaller corpus, but carry more risk.</p>
        </div>
      </>
    ),
    3: (
      <>
        <div className="ret-grid2">
          <Field label="Savings / MF / FD">
            <RupeeInput value={form.currentSavings} onChange={(v) => upd("currentSavings", v)} />
          </Field>
          <Field label="EPF / PF Balance">
            <RupeeInput value={form.epf} onChange={(v) => upd("epf", v)} placeholder="0" />
          </Field>
          <Field label="PPF Balance">
            <RupeeInput value={form.ppf} onChange={(v) => upd("ppf", v)} placeholder="0" />
          </Field>
          <Field label="NPS Balance">
            <RupeeInput value={form.nps} onChange={(v) => upd("nps", v)} placeholder="0" />
          </Field>
        </div>
        <Field label="Stocks / Real Estate / Others">
          <RupeeInput value={form.other} onChange={(v) => upd("other", v)} placeholder="0" />
        </Field>
        {existingTotal > 0 && (
          <div className="ret-existing-total">
            Total existing wealth: <strong>{inr(existingTotal)}</strong>
          </div>
        )}
      </>
    ),
  };

  return (
    <div className="ret-page">

      {/* ── HERO ── */}
      <div className="ret-hero">
        <span className="ret-badge">🎯 Financial Independence · FY 2026–27</span>
        <h1>
  Retirement{" "}
  <span className="choose-title-accent">Calculator</span>
</h1>
        <p>Plan the retirement you deserve — inflation-adjusted, India-specific, comprehensive</p>
      </div>

      <div className="ret-wrapper">

        {/* ════ FORM PANEL ════ */}
        <div className="ret-panel">

          {/* Tab bar */}
          <div className="ret-tab-bar">
            {FORM_TABS.map((t, i) => (
              <button key={t}
                className={`ret-tab${formTab === i ? " active" : ""}`}
                onClick={() => setFormTab(i)}>
                <span className="ret-tab-icon">{FORM_ICONS[i]}</span>
                {t}
              </button>
            ))}
          </div>

          {/* Tab body */}
          <div className="ret-panel-body">
            {tabContent[formTab]}
          </div>

          {/* Step nav */}
          <div className="ret-step-nav">
            {formTab > 0 && (
              <button className="ret-btn-ghost" onClick={() => setFormTab(formTab - 1)}>← Back</button>
            )}
            {formTab < FORM_TABS.length - 1 ? (
              <button className="ret-btn-primary" onClick={() => setFormTab(formTab + 1)}>
                Next: {FORM_TABS[formTab + 1]} →
              </button>
            ) : (
              <button className="ret-btn-primary ret-btn-calc" onClick={calculate}>
                Plan My Retirement →
              </button>
            )}
          </div>

          {/* Progress dots */}
          <div className="ret-dots">
            {FORM_TABS.map((_, i) => (
              <div key={i}
                className={`ret-dot${formTab === i ? " active" : ""}`}
                onClick={() => setFormTab(i)} />
            ))}
          </div>
        </div>

        {/* ════ RESULTS ════ */}
        <div className="ret-result-col">
          {!result ? (
            <div className="ret-empty">
              <div className="ret-empty-icon">🎯</div>
              <h3>Discover your retirement number</h3>
              <p>Find out exactly how much you need, and whether you're on track for financial independence</p>
              <div className="ret-rules">
                {[
                  ["📌", "Rule of 25", "Save 25× your annual expenses for a secure retirement"],
                  ["📊", "Rule of 4%", "Withdraw 4% of corpus annually — funds last 25–30 years"],
                  ["📈", "Start Early", "₹5,000/month at 25 = 4× more than ₹10,000/month at 40"],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="ret-rule">
                    <span className="ret-rule-icon">{icon}</span>
                    <div><strong>{title}</strong><span>{desc}</span></div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="ret-results-wrap">

              {/* Track banner */}
              <div className={`ret-track-banner${result.isOnTrack ? " on-track" : " off-track"}`}>
                <div className="ret-track-icon">{result.isOnTrack ? "✅" : "⚠️"}</div>
                <div>
                  <div className="ret-track-title">
                    {result.isOnTrack ? "You're On Track! 🎉" : "Retirement Gap Detected"}
                  </div>
                  <div className="ret-track-sub">
                    {result.isOnTrack
                      ? `Surplus of ${crore(result.surplus)} — keep investing!`
                      : `Shortfall of ${crore(Math.abs(result.surplus))} · Need ${inr(result.addlMonthly)} extra/month`}
                  </div>
                </div>
              </div>

              {/* Mini stat cards */}
              <div className="ret-stat-grid">
                <StatCard label="Corpus Required" value={crore(result.corpusNeeded)}
                  note={`for ${result.retYrs} yrs of retirement`} accent colorClass="ret-orange" />
                <StatCard label="Corpus You'll Build" value={crore(result.totalCorpus)}
                  colorClass={result.isOnTrack ? "ret-green" : "ret-red"} />
                <StatCard label="Future Monthly Expense" value={inr(result.futureMoExp)}
                  note={`at ${form.inflation}% inflation`} colorClass="ret-orange" />
                <StatCard label="Monthly Income from Corpus" value={inr(result.monthlyIncome)}
                  colorClass={result.monthlyIncome >= result.futureMoExp ? "ret-green" : "ret-red"} />
              </div>

              {/* Result tabs */}
              <div className="ret-panel">
                <div className="ret-tab-bar">
                  {RES_TABS.map((t, i) => (
                    <button key={t}
                      className={`ret-tab${resTab === i ? " active" : ""}`}
                      onClick={() => setResTab(i)}>{t}</button>
                  ))}
                </div>

                {/* Overview */}
                {resTab === 0 && (
                  <div className="ret-panel-body">
                    {/* Corpus compare bars */}
                    <div className="ret-cc-block">
                      <div className="ret-section-label">Corpus vs Requirement</div>
                      <div className="ret-cc-row">
                        <span className="ret-cc-lbl">Required</span>
                        <div className="ret-cc-track">
                          <div className="ret-cc-fill ret-cc-fill--req" style={{ width: "100%" }} />
                        </div>
                        <strong className="ret-orange">{crore(result.corpusNeeded)}</strong>
                      </div>
                      <div className="ret-cc-row">
                        <span className="ret-cc-lbl">You'll Build</span>
                        <div className="ret-cc-track">
                          <div
                            className={`ret-cc-fill ${result.isOnTrack ? "ret-cc-fill--built" : "ret-cc-fill--short"}`}
                            style={{ width: `${Math.min(100, (result.totalCorpus / result.corpusNeeded) * 100).toFixed(1)}%` }}
                          />
                        </div>
                        <strong className={result.isOnTrack ? "ret-green" : "ret-red"}>
                          {crore(result.totalCorpus)}
                        </strong>
                      </div>
                      <div className="ret-coverage-note">
                        📅 At {form.postReturn}% post-retirement returns, your corpus covers ~{Math.round(Math.min(result.coverageYears, 60))} years of expenses
                      </div>
                    </div>
                  </div>
                )}

                {/* Corpus Build */}
                {resTab === 1 && (
                  <div className="ret-panel-body">
                    <div className="ret-section-label">How Your Corpus is Built</div>
                    {[
                      { label: "Existing Wealth (Future Value)", value: result.fvExisting, color: "#3b82f6" },
                      { label: "Future Monthly Savings (FV)", value: result.fvSavings, color: "#263012" },
                    ].filter(x => x.value > 0).map((item) => {
                      const pct = (item.value / result.totalCorpus * 100).toFixed(1);
                      return (
                        <div key={item.label} className="ret-comp-row">
                          <div className="ret-comp-meta">
                            <span className="ret-comp-dot" style={{ background: item.color }} />
                            <span>{item.label}</span>
                            <span className="ret-comp-pct">{pct}%</span>
                            <strong>{crore(item.value)}</strong>
                          </div>
                          <div className="ret-comp-track">
                            <div className="ret-comp-fill" style={{ width: `${pct}%`, background: item.color }} />
                          </div>
                        </div>
                      );
                    })}
                    <div className="ret-stack-bar">
                      {[
                        { v: result.fvExisting, c: "#3b82f6" },
                        { v: result.fvSavings,  c: "#263012" },
                      ].filter(x => x.v > 0).map((x, i) => (
                        <div key={i} className="ret-stack-seg"
                          style={{ width: `${(x.v / result.totalCorpus) * 100}%`, background: x.c }} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Year-wise */}
                {resTab === 2 && (
                  <div className="ret-table-wrap">
                    <table className="ret-table">
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Age</th>
                          <th>Corpus Built</th>
                          <th>Target Needed</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.yearlyData
                          .filter((_, i) => i % 3 === 0 || i === result.yearlyData.length - 1)
                          .map((row, i) => (
                            <tr key={i} className={row.age === +form.retirementAge ? "ret-tr--retire" : ""}>
                              <td className="ret-td-c">{row.y}</td>
                              <td className="ret-td-c">{row.age}</td>
                              <td className="ret-td-r ret-blue">{crore(row.corpus)}</td>
                              <td className="ret-td-r">{crore(row.needed)}</td>
                              <td className="ret-td-c">
                                {row.corpus >= row.needed
                                  ? <span className="ret-status-ok">✅ Ahead</span>
                                  : <span className="ret-status-gap">Gap: {crore(row.needed - row.corpus)}</span>}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}